var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { createDemoPeriodComparisonMatcher } from '@web/lib/demo/utils';
import { diffComparisonType } from '@workalizer/shared/types';
import { calculateAverage, formatDuration, getInterval, getPercentageDiff, getPreviousPeriod, groupByPeriod } from '@workalizer/shared/utils';
import { endOfDay, startOfDay } from 'date-fns';
var CURRENT_PERIOD_COUNTS = [
    { attended: 4, skipped: 1, attendedDuration: 7200, skippedDuration: 1800 },
    { attended: 3, skipped: 2, attendedDuration: 5400, skippedDuration: 3600 },
    { attended: 5, skipped: 1, attendedDuration: 9000, skippedDuration: 2700 },
    { attended: 4, skipped: 2, attendedDuration: 7200, skippedDuration: 3600 },
    { attended: 3, skipped: 3, attendedDuration: 5400, skippedDuration: 5400 },
    { attended: 5, skipped: 1, attendedDuration: 9000, skippedDuration: 1800 },
    { attended: 4, skipped: 2, attendedDuration: 7200, skippedDuration: 3600 }
];
var PREV_PERIOD_COUNTS = [
    { attended: 3, skipped: 2, attendedDuration: 5400, skippedDuration: 3600 },
    { attended: 4, skipped: 1, attendedDuration: 7200, skippedDuration: 1800 },
    { attended: 2, skipped: 3, attendedDuration: 3600, skippedDuration: 5400 },
    { attended: 3, skipped: 2, attendedDuration: 5400, skippedDuration: 3600 },
    { attended: 4, skipped: 2, attendedDuration: 7200, skippedDuration: 3600 },
    { attended: 2, skipped: 2, attendedDuration: 3600, skippedDuration: 3600 },
    { attended: 3, skipped: 1, attendedDuration: 5400, skippedDuration: 1800 }
];
var TEAM_PERIOD_TOTALS = [
    { attended: 28, skipped: 8 },
    { attended: 24, skipped: 12 },
    { attended: 32, skipped: 6 },
    { attended: 26, skipped: 10 },
    { attended: 22, skipped: 14 },
    { attended: 30, skipped: 8 },
    { attended: 24, skipped: 12 }
];
var TEAM_USER_COUNT = 8;
var sumPeriodCounts = function (periods) {
    return periods.reduce(function (acc, period) { return ({
        attended: acc.attended + period.attended,
        skipped: acc.skipped + period.skipped,
        attendedDuration: acc.attendedDuration + period.attendedDuration,
        skippedDuration: acc.skippedDuration + period.skippedDuration
    }); }, {
        attended: 0,
        skipped: 0,
        attendedDuration: 0,
        skippedDuration: 0
    });
};
export var scheduledMeetAttendanceRate = function (input) {
    var interval = getInterval(input.dateFrom, input.dateTo);
    var dateFrom = startOfDay(input.dateFrom);
    var dateTo = endOfDay(input.dateTo);
    var _a = getPreviousPeriod(input.dateFrom, input.dateTo), prevPeriodDateFrom = _a.prevPeriodDateFrom, prevPeriodDateTo = _a.prevPeriodDateTo;
    var periods = groupByPeriod([], dateFrom, dateTo, interval);
    var previousPeriods = groupByPeriod([], startOfDay(prevPeriodDateFrom), endOfDay(prevPeriodDateTo), interval);
    var periodMatcher = createDemoPeriodComparisonMatcher(input, interval, periods, previousPeriods);
    var periodsData = periods.map(function (period, index) {
        var _a;
        var currentData = CURRENT_PERIOD_COUNTS[index % CURRENT_PERIOD_COUNTS.length];
        var prevPeriod = periodMatcher.getItem(index, previousPeriods);
        var previousIndex = prevPeriod
            ? previousPeriods.indexOf(prevPeriod)
            : index;
        var prevData = PREV_PERIOD_COUNTS[previousIndex % PREV_PERIOD_COUNTS.length];
        return {
            period: period.date,
            previousPeriodLabel: (_a = prevPeriod === null || prevPeriod === void 0 ? void 0 : prevPeriod.date) !== null && _a !== void 0 ? _a : '',
            attended: currentData.attended,
            skipped: currentData.skipped,
            prevAttended: prevData.attended,
            prevSkipped: prevData.skipped
        };
    });
    var currentTotals = sumPeriodCounts(periods.map(function (_, index) { return CURRENT_PERIOD_COUNTS[index % CURRENT_PERIOD_COUNTS.length]; }));
    var previousTotals = sumPeriodCounts(periods.map(function (_, index) {
        var prevPeriod = periodMatcher.getItem(index, previousPeriods);
        var previousIndex = prevPeriod
            ? previousPeriods.indexOf(prevPeriod)
            : index;
        return PREV_PERIOD_COUNTS[previousIndex % PREV_PERIOD_COUNTS.length];
    }));
    var teamAttendedTotal = periods.reduce(function (sum, _, index) {
        var teamTotals = TEAM_PERIOD_TOTALS[index % TEAM_PERIOD_TOTALS.length];
        return sum + teamTotals.attended;
    }, 0);
    var teamSkippedTotal = periods.reduce(function (sum, _, index) {
        var teamTotals = TEAM_PERIOD_TOTALS[index % TEAM_PERIOD_TOTALS.length];
        return sum + teamTotals.skipped;
    }, 0);
    var teamAvgAttended = calculateAverage(teamAttendedTotal, TEAM_USER_COUNT);
    var teamAvgSkipped = calculateAverage(teamSkippedTotal, TEAM_USER_COUNT);
    var teamAvgDiffOptions = {
        comparisonType: diffComparisonType.teamAverage
    };
    var result = {
        periods: periodsData,
        totals: {
            attended: {
                current: currentTotals.attended,
                currentDuration: formatDuration(currentTotals.attendedDuration, 'seconds'),
                previous: getPercentageDiff(previousTotals.attended, currentTotals.attended),
                teamAverage: input.userId
                    ? getPercentageDiff(teamAvgAttended, currentTotals.attended, teamAvgDiffOptions)
                    : null
            },
            skipped: {
                current: currentTotals.skipped,
                currentDuration: formatDuration(currentTotals.skippedDuration, 'seconds'),
                previous: getPercentageDiff(previousTotals.skipped, currentTotals.skipped),
                teamAverage: input.userId
                    ? getPercentageDiff(teamAvgSkipped, currentTotals.skipped, teamAvgDiffOptions)
                    : null
            }
        }
    };
    if (!input.userId) {
        return result;
    }
    return __assign(__assign({}, result), { teamPeriods: periods.map(function (period, index) {
            var teamTotals = TEAM_PERIOD_TOTALS[index % TEAM_PERIOD_TOTALS.length];
            return {
                period: period.date,
                teamAverageAttended: Math.round(teamTotals.attended / TEAM_USER_COUNT),
                teamAverageSkipped: Math.round(teamTotals.skipped / TEAM_USER_COUNT)
            };
        }) });
};
